<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'upcomingHandovers' }">{{ t('back') }}</ButtonBack>
    <CVButton
      v-if="transfer"
      outline
      :to="{
        name: 'transferEdit',
        params: {
          transferType: transfer.type,
          allocationId: transfer.transferOf?.id,
          transferId: transfer?.id,
        },
      }"
      >{{ t('editTransfer') }}
    </CVButton>
  </ButtonBackContainer>
  <section class="carvia-container flex flex-col justify-center">
    <div class="flex w-full justify-center space-y-3">
      <div v-if="transfer" class="flex-1">
        <h2>
          {{ t('transferOverview') }}
        </h2>

        <BasicTransferOverview :transfer="transfer" />

        <Divider />

        <div class="mt-4 grid gap-4 md:grid-cols-3">
          <div class="space-y-4">
            <LabeledText :label="t('transferStartDate')">{{ formattedStartDate }}</LabeledText>
            <TransferAddress :transfer="transfer" mode="pickup" />
          </div>

          <div class="space-y-4">
            <LabeledText :label="t('transferEndDate')">{{ formattedEndDate }}</LabeledText>
            <TransferAddress :transfer="transfer" mode="dropoff" />
          </div>

          <div class="space-y-4">
            <LabeledText :label="t('estimatedDrivingTime')">
              {{ estimatedDrivingTime }}
            </LabeledText>

            <LabeledText :label="t('estimatedOnSiteTime')">{{ estimatedOnSiteTime }}</LabeledText>
          </div>
        </div>

        <Divider />

        <div class="mt-4 grid gap-4 md:grid-cols-3">
          <LabeledText :label="t('renter')"
            >{{ transfer.transferOf?.booking?.customerData?.firstName ?? 'n/a' }}
            {{ transfer.transferOf?.booking?.customerData?.lastName ?? 'n/a' }}</LabeledText
          >
          <LabeledText :label="t('phoneCustomer')">
            <PhoneLink :phone-number="transfer.transferOf?.booking?.customerData?.phone" />
          </LabeledText>

          <LabeledText :label="t('emailCustomer')">{{
            transfer.transferOf?.booking?.customer?.email ?? 'n/a'
          }}</LabeledText>
        </div>

        <Divider />

        <VehicleAndTimes :booking-or-allocation="transfer.transferOf!" :car="transfer.car" />
      </div>
      <div v-else>
        <Spinner />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import TransferAddress from '@/components/booking-forms/TransferAddress.vue';
import VehicleAndTimes from '@/components/booking-forms/VehicleAndTimes.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import Divider from '@/components/Divider.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import LabeledText from '@/components/LabeledText.vue';
import PhoneLink from '@/components/PhoneLink.vue';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store';
import BasicTransferOverview from '@/views/handover/components/transfer/components/BasicTransferOverview.vue';
import { DateTime } from 'luxon';
import { computed, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useAllocationOfTypeOrReroute } from '@/queries/use-allocations.ts';
import { AllocationType } from '@carvia/ros-client-types';

const uiStore = useUiStore();
const { t } = useI18n();
const route = useRoute();
const transferId = ref(route.params.id as string);
const { data: transfer } = useAllocationOfTypeOrReroute(
  transferId,
  true,
  [AllocationType.TRANSFER_DELIVERY, AllocationType.TRANSFER_RETURN],
  'upcomingHandovers',
);

const startDate = computed(() => transfer.value?.startDate);
const endDate = computed(() => transfer.value?.endDate);
const formattedStartDate = useFormattedDate(startDate);
const formattedEndDate = useFormattedDate(endDate);

watchEffect(() => {
  if (transfer.value) {
    const title = t(
      transfer.value?.type === AllocationType.TRANSFER_DELIVERY ? 'delivery' : 'return',
    );
    const { car } = transfer.value;
    const customerData = transfer.value.transferOf?.booking?.customerData;
    uiStore.setHeaderTitle(
      title,
      `${customerData?.firstName ?? 'n/a'} ${customerData?.lastName ?? 'n/a'}`,
      `${car.vehicleType?.make.name} ${car.vehicleType?.model}`,
    );
  } else {
    uiStore.setHeaderTitle(t('transfer'));
  }
});

const estimatedDrivingTime = computed(() => {
  if (!transfer.value) return undefined;
  const start = DateTime.fromISO(transfer.value?.startDate);
  const end = DateTime.fromISO(transfer.value?.endDate);
  return end.diff(start, 'hours').toHuman();
});
const estimatedOnSiteTime = computed(() => {
  if (!transfer.value?.transferOf?.startDate) return undefined;
  const start = DateTime.fromISO(transfer.value.transferOf.startDate);
  const arrival = DateTime.fromISO(transfer.value.endDate);
  return start.diff(arrival, 'hours').toHuman();
});
</script>

<i18n lang="json">
{
  "en": {
    "booking": "Booking",
    "transfer": "Transfer",
    "delivery": "Delivery",
    "return": "Return",
    "transferOverview": "Transfer overview",
    "transferStartDate": "Transfer start date",
    "transferEndDate": "Transfer end date",
    "estimatedDrivingTime": "Estimated driving time",
    "estimatedOnSiteTime": "Estimated on-site time",
    "renter": "Renter",
    "phoneCustomer": "Phone customer",
    "emailCustomer": "Email customer",
    "editTransfer": "Edit transfer"
  },
  "de": {
    "booking": "Buchung",
    "transfer": "Transfer",
    "delivery": "Anlieferung",
    "return": "Rückführung",
    "transferOverview": "Transferübersicht",
    "transferStartDate": "Transferbeginn",
    "transferEndDate": "Transferende",
    "estimatedDrivingTime": "Geschätzte Fahrzeit",
    "estimatedOnSiteTime": "Geschätzte Zeit vor Ort",
    "renter": "Mieter",
    "phoneCustomer": "Telefon Kunde",
    "emailCustomer": "Email Kunde",
    "editTransfer": "Transfer bearbeiten"
  },
  "es": {
    "booking": "Reserva",
    "transfer": "Traslado",
    "delivery": "Entrega",
    "return": "Recogida",
    "transferOverview": "Vista general del traslado",
    "transferStartDate": "Inicio del traslado",
    "transferEndDate": "Fin del traslado",
    "estimatedDrivingTime": "Tiempo de conducción estimado",
    "estimatedOnSiteTime": "Tiempo estimado in situ",
    "renter": "Arrendatario",
    "phoneCustomer": "Teléfono del cliente",
    "emailCustomer": "Correo del cliente",
    "editTransfer": "Editar traslado"
  }
}
</i18n>
